<script setup lang="ts">
import useVuelidate from '@vuelidate/core';
import { storeToRefs } from 'pinia';
import { ModellingType } from '../../../types/Order';
import { vMinValue, vNumeric, vRequired, vRequiredId, vRequiredIf } from '../../../utils/vuelidateValidators';
import useDataQuery from '../../../composables/useDataQuery';
import { getAllmaterials } from '../../../dataProviders/admin/materials';
import { useOrderStore } from '../../../stores/orderForm';
import { getAllColorGroups } from '../../../dataProviders/admin/colorGroups';
import { getSettings } from '../../../dataProviders/admin/settings';
import type { PricingFields } from '../../../utils/price';
import { calcOrderPrice } from '../../../utils/price';
import { useUserStore } from '../../../stores/user';

const user = useUserStore();
const store = useOrderStore();
const { machineType, order, disabledPreview, isPreviewMode } = storeToRefs(store);
const { data: dentalMaterials, isLoading: areMaterialsLoading } = useDataQuery({ query: getAllmaterials, uniqueKey: 'formMaterials', useCache: 'short' });
const { data: dentalColors, isLoading: areColorsLoadinng } = useDataQuery({ query: getAllColorGroups, uniqueKey: 'colorGroups' });
const { data: settigs, isLoading: areSettingsLoading } = useDataQuery({ query: getSettings, uniqueKey: 'settings', placeholder: { priceFullctr: 0, priceCoping: 0, price3D: 0, priceModels: 0 } });

const colorsData = computed(() => {
  if (areColorsLoadinng.value || dentalColors.value === undefined || !order.value.material_id) {
    return {
      options: [],
      areAvailable: true
    };
  }

  const selected = dentalMaterials.value?.find(e => e.id === order.value.material_id);
  if (!selected) {
    return {
      options: [],
      areAvailable: true
    };
  }

  const options = dentalColors.value?.find(group => group.id === selected.color_id)?.colors ?? [];
  const areAvailable = options.length > 0;
  return {
    options,
    areAvailable
  };
});

const rules = computed(() => {
  const r = {
    number_parts: { required: vRequired(), minValue: vMinValue(1), numeric: vNumeric() },
    material_id: { },
    color: { required: vRequiredIf(colorsData.value.areAvailable && machineType.value === 'milling') },
    type: { required: vRequiredIf(order.value.modeling) },
  };
  if (machineType.value === 'milling') {
    r.material_id = { required: vRequiredId() };
  }

  return r;
});

const v$ = useVuelidate(rules, order);

watch(() => order.value.modeling, (isSelected) => {
  order.value.type = isSelected ? ModellingType.fullcontour : '';
});

watch(() => order.value.material_id, () => {
  order.value.color = ''; // reset color on material change
});

// Picing & tracking
const pricingFields = computed<PricingFields>(() => ({
  material_id: order.value.material_id,
  number_parts: order.value.number_parts,
  modeling: order.value.modeling,
  type: order.value.type,
  machine_type: order.value.machine_type,
}));

// Track updates for updating the price
function calcAndUpdatePrice(pricingField: Partial<PricingFields>) {
  const price = calcOrderPrice({ ...order.value, ...pricingField }, dentalMaterials.value, settigs.value);
  if (price) {
    order.value.price = price;
  }
}

/**
 * Calculate the auto price on start when we're CREATING new order
 * We need this because in Printing we don't have dynamic options (like material & color) and we need to begin the calc from the start
 * When an order is already created we want to see the original/saved price
 * If the admin changes any pricing field we should run the calculation and override with the auto price
 */
// if (order.status === 'pending' && 'file_ids' in order && !('created_at' in order)) {
watch(
  [() => areMaterialsLoading.value, () => areColorsLoadinng.value, () => areSettingsLoading.value],
  (areLoading) => {
    if (areLoading.every(e => e === false)) {
      if (order.value.status === 'pending' && (isPreviewMode.value === false || order.value.price === 0 || user.profile.access_level === 'User')) {
        calcAndUpdatePrice(pricingFields.value);
      }
      store.setFetchingResourcess(false);
    }
  },
  { immediate: true }
);

watch(pricingFields, updatedFields => calcAndUpdatePrice(updatedFields), { deep: true });
</script>

<template>
  <Fieldset class="mb-6" :pt="{ legend: 'bg-gray-200 border border-gray-300 hover:bg-gray-300' }">
    <template #legend>
      <div class="align-items-center flex gap-2 px-2">
        <span class="font-bold">Изработка</span>
        <i class="pi pi-file-edit !leading-[unset]" />
      </div>
    </template>

    <div class="mb-6">
      <FormItem
        field="number_parts"
        :label="$t('orderForm.number_parts.label')"
        :v$="v$"
        required
      >
        <NumberInput
          v-model="order.number_parts"
          class="w-full"
          input-id="number_parts"
          :min="1"
          :step="1"
          :pt="{ input: 'w-full' }"
          :disabled="disabledPreview"
        />
      </FormItem>
    </div>

    <div class="grid grid-cols-3 mb-6">
      <FormItem class="col-span-2" field="modelling" :label="$t('orderForm.modelling.label')">
        <Checkbox
          id="modelling"
          v-model="order.modeling"
          class="mr-2"
          :binary="true"
          :disabled="disabledPreview"
        />

        {{ $t('orderForm.modelling.text') }}
      </FormItem>

      <FormItem v-if="order.modeling" field="type" label="Тип">
        <ul class="m-0 flex list-none gap-3 p-0">
          <li class="align-items-center flex">
            <RadioButton
              v-model="order.type"
              :disabled="disabledPreview"
              :input-id="ModellingType.fullcontour"
              :name="ModellingType.fullcontour"
              :value="ModellingType.fullcontour"
            />
            <label :for="ModellingType.fullcontour" class="ml-2">{{ $t(`orderForm.type.${ModellingType.fullcontour}`)
            }}</label>
          </li>
          <li class="align-items-center flex">
            <RadioButton
              v-model="order.type"
              :disabled="disabledPreview"
              :input-id="ModellingType.coping"
              :name="ModellingType.coping"
              :value="ModellingType.coping"
            />
            <label :for="ModellingType.coping" class="ml-2">{{ $t(`orderForm.type.${ModellingType.coping}`) }}</label>
          </li>
        </ul>
      </FormItem>
    </div>

    <div v-if="machineType === 'milling'" class="mb-6 pd-form-cols-2">
      <FormItem
        field="material_id"
        :label="$t('orderForm.material_id.label')"
        :v$="v$"
        required
      >
        <Dropdown
          v-model="order.material_id"
          filter
          :loading="areMaterialsLoading"
          :options="dentalMaterials"
          option-label="name"
          option-value="id"
          :placeholder="$t('orderForm.material_id.placeholder')"
          class="w-full"
          :disabled="disabledPreview || areMaterialsLoading"
        />
      </FormItem>

      <FormItem
        field="color"
        :label="$t('orderForm.color.label')"
        :v$="v$"
        :required="colorsData.areAvailable"
      >
        <Dropdown
          v-model="order.color"
          :loading="areColorsLoadinng"
          :disabled="areColorsLoadinng || disabledPreview || !colorsData.areAvailable || !order.material_id"
          :options="colorsData.options"
          :placeholder="colorsData.areAvailable ? $t('orderForm.color.placeholder') : $t('orderForm.color.noColor')"
          class="w-full"
        />
      </FormItem>
    </div>

    <FormItem field="comment" :label="$t('orderForm.comment.label')">
      <Textarea
        v-model="order.comment"
        class="w-full resize-none"
        rows="3"
        :disabled="disabledPreview"
      />
    </FormItem>
  </Fieldset>
</template>
